import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Box, Divider, Grid, Stack, SvgIcon, Typography, useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { animated, useSpring } from 'react-spring'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import AngleDownIcon from '@hapana/shared/web/styles/assets/svgs/angle-down-small.svg'
import cameraIcon from '@hapana/shared/web/styles/assets/svgs/camera.svg'
import note from '@hapana/shared/web/styles/assets/svgs/note.svg'
import packages from '@hapana/shared/web/styles/assets/svgs/package-small.svg'
import pinLocation from '@hapana/shared/web/styles/assets/svgs/map-marker-alt-small.svg'
import schedule from '@hapana/shared/web/styles/assets/svgs/schedule-small.svg'
import shoppingCart from '@hapana/shared/web/styles/assets/svgs/shopping-cart-small.svg'
import { ALERT_TYPES } from '@hapana/shared/web/components/shared/alerts/alert.constant'
import Avatar from '@hapana/shared/web/components/shared/avatar/avatar.component'
import {
  AVATAR_SIZE,
  AVATAR_TYPES,
  AVATAR_VARIANT,
} from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import {
  FAILED_PAYMENT_REASONS,
  SEVERITY,
  SIZE,
} from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_THEME,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { PAYMENT_TYPE } from '@hapana/shared/web/components/shared/individual-alerts/individual-alerts.constants'
import Skeleton from '@hapana/shared/web/components/shared/skeleton/skeleton.component'
import { SKELETON_VARIANTS } from '@hapana/shared/web/components/shared/skeleton/skeleton.constants'
import Tooltip from '@hapana/shared/web/components/shared/tooltip/tooltip.component'
import { TOOLTIP_POSITION } from '@hapana/shared/web/components/shared/tooltip/tooltip.constants'
import { TIME_FORMATS } from '@hapana/shared/constants/date.constants'
import { useSiteNames } from '@hapana/shared/web/hooks/site.hooks'
import useSiteStore from '_stores/site.store'
import {
  useSiteConfiguration,
  useTimeFormat,
} from '@hapana/shared/web/hooks/siteConfiguration.hooks'
import { ROUTES } from '../../../app.constants'
import Badges from '@components/check-in-widget/badges/badges.component'
import AddNoteDialog from '@pages/clients/components/client-notes/dialogs/add-note.component'
import ModalAlerts from '@components/check-in-widget/modal-alerts/modal-alerts.component'
import { TITLE as CHECK_IN_TITLE } from '@components/check-in-widget/modal-check-in/modal-check-in.constants'
import ModalUploadPhoto from '@hapana/shared/web/components/shared/modal-upload-photo/modal-upload-photo.component'
import PaymentsList from '@components/check-in-widget/payments-list/payments-list.component'
import ViewAllAlerts from '@components/check-in-widget/view-alerts/view-alerts.component'
import { useClientActivePackages } from '@hapana/shared/web/hooks/client.hooks'
import { CHECK_IN_STATUSES } from '@hapana/shared/constants/packages.constants'
import { resolved, mutation as gqtyMutation } from '@hapana/shared/web/services/gqty'
import { uploadToSignedUrl } from '@hapana/shared/web/services/upload.service'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { useSiteEnableTanningFeatures } from '_hooks/sites.hooks'
import { useMe } from '_hooks/me.hooks'

import { FADE_CONFIG } from './most-recent-card.constants'
import { handleStylesWithProps, styles } from './most-recent-card.styles'
import { isFirstSession } from './most-recent-card.util'
import { EquipmentsAvailable } from './equipments-available/equipments-available.component'

const AnimatedBox = animated(Box)

const MostRecentCardAnimated = ({
  profileImg,
  name,
  firstName,
  birthday,
  status,
  date,
  checkInTime,
  membership,
  location,
  nextSession,
  payments,
  clientId,
  totalCheckInCount,
  siteInformation,
  isInitiallyOpen,
  failReason,
  index,
  homeSites,
  siteNotes,
  siteAlertedProperties,
  formsPending,
  sitesInIntroOffer,
  checkInId,
}) => {
  const [isOpen, setIsOpen] = useState(!!isInitiallyOpen)
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { siteId: paramSiteId } = useParams()
  const { data: configuration } = useSiteConfiguration()
  const { data: siteEnableTanningFeatures } = useSiteEnableTanningFeatures()
  const { data: homeLocations, isIdle: isHomeLocation } = useSiteNames(homeSites)
  const homeLocationNames = homeLocations?.map((homeLocation) => homeLocation.name)

  const { data: timeFormatData } = useTimeFormat()
  const { pushDialog } = useDialog()
  const { openToast } = useToast()
  const { t } = useTranslation(['checkIn', 'core', 'common'])
  const resolvedSiteId = paramSiteId || siteId
  const profileUrl = resolvedSiteId && clientId ? `/s/${resolvedSiteId}/clients/${clientId}` : null
  const saleUrl =
    resolvedSiteId && clientId
      ? `/s/${resolvedSiteId}/${ROUTES.retailDashboard.children.newSale.path}?clientId=${clientId}`
      : resolvedSiteId
        ? `/s/${resolvedSiteId}/${ROUTES.retailDashboard.children.newSale.path}`
        : null
  const [isUploadingImage, setIsUploadingImage] = useState(false)
  const { data: meData } = useMe()

  const handleImageUpload = useCallback(
    (data, callbacks) => {
      const userId = meData?.me?.id
      if (!userId) {
        openToast({ title: t('common:somethingWentWrong'), severity: 'error' })
        return
      }
      setIsUploadingImage(true)
      resolved(
        () => {
          const result = gqtyMutation.updateClientProfileImage({
            clientId,
            input: { format: data.fileExtension },
          })
          return {
            tempUrl: result.tempUrl,
            requestedBy: result.requestedBy,
            userType: result.userType,
          }
        },
        { noCache: true }
      )
        .then((response) => uploadToSignedUrl(response, data.file))
        .then((uploadResponse) => {
          if (!uploadResponse.ok) {
            throw new Error('Upload failed')
          }
          setIsUploadingImage(false)
          openToast({ title: t('core:userProfile.profileImageProcessing'), severity: 'info' })
          callbacks?.onSuccess?.()
        })
        .catch((error) => {
          console.error('Image upload failed:', error)
          setIsUploadingImage(false)
          openToast({ title: t('common:somethingWentWrong'), severity: 'error' })
          callbacks?.onError?.(error)
        })
    },
    [clientId, meData?.me?.id, openToast, t]
  )

  const clientImageMutation = {
    mutate: handleImageUpload,
    isLoading: isUploadingImage,
    responseKey: 'updateClientProfileImage',
  }

  const { data: packagesData } = useClientActivePackages(clientId)
  const isStatusUnknownAndOpen = status?.toUpperCase() === CHECK_IN_STATUSES.unknown && isOpen
  const isStatusUnknownAndClosed = status?.toUpperCase() === CHECK_IN_STATUSES.unknown && !isOpen

  const hasNoClientRecord =
    status?.toUpperCase() === CHECK_IN_STATUSES.fail &&
    failReason?.toUpperCase() ===
      FAILED_PAYMENT_REASONS.NO_VALID_MEMBERSHIP_OR_BOOKING_AND_NO_CLIENT_RECORD.key

  const hasNotesAndProperties =
    siteNotes?.find((s) => s.siteId === siteId) ||
    siteAlertedProperties?.find((s) => s.siteId === siteId)

  const [avatarAnimationStyles, avatarAnimationApi] = useSpring(() => ({}))
  const [fadeAnimationStyles, fadeAnimationApi] = useSpring(() => ({
    ...styles.fadeStyles,
    ...FADE_CONFIG,
  }))
  const [revertedFadeAnimationStyles, revertedFadeAnimationApi] = useSpring(() => ({
    ...styles.revertedFadeStyles,
    ...FADE_CONFIG,
  }))
  const [rotateAnimationStyles, rotateAnimationApi] = useSpring(() => styles.rotateStyles)
  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const hasTanningFeatures = siteEnableTanningFeatures?.adminConfigurations?.enableTanningFeatures

  const stylesWithProps = useMemo(() => {
    return handleStylesWithProps({ isOpen })
  }, [isOpen])

  useEffect(() => {
    const avatarApiConfig = matchesDesktop
      ? stylesWithProps.avatarAnimation
      : stylesWithProps.avatarAnimationMobile
    const fadeAnimationApiConfig = stylesWithProps.fadeAnimation
    const revertedFadeAnimationApiConfig = stylesWithProps.revertedFadeAnimation
    const rotateAnimationApiConfig = stylesWithProps.rotateAnimation

    avatarAnimationApi.start(avatarApiConfig)
    fadeAnimationApi.start({
      ...fadeAnimationApiConfig,
      config: {
        clamp: true,
      },
    })
    revertedFadeAnimationApi.start(revertedFadeAnimationApiConfig)
    rotateAnimationApi.start(rotateAnimationApiConfig)
  }, [
    isOpen,
    avatarAnimationApi,
    fadeAnimationApi,
    rotateAnimationApi,
    stylesWithProps,
    matchesDesktop,
    revertedFadeAnimationApi,
  ])

  useEffect(() => {
    setIsOpen(isInitiallyOpen)
  }, [isInitiallyOpen])

  const handleBannerOptions = () => {
    if (status?.toUpperCase() === SEVERITY.SUCCESS.toUpperCase()) {
      if (payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */) {
        return {
          severity: SEVERITY.WARNING,
          message: t('checkIn:status.valid'),
        }
      }
      return {
        severity: SEVERITY.SUCCESS,
        message: t('checkIn:status.valid'),
      }
    }
    return {
      severity: SEVERITY.ERROR,
      message: t('checkIn:status.invalid'),
    }
  }

  const { message, description, severity } = {
    ...handleBannerOptions(),
    description:
      payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */
        ? `${t('withAlerts')}`
        : '',
  }

  const currentTimezone = configuration?.timezone
  const locationWidth = location?.length > 12 ? 12 : 'auto'
  const sessionWidth = nextSession?.title?.length > 10 ? 12 : 'auto'
  const filteredFormsPending = formsPending
    ?.filter((form) => form.siteId === siteId)
    .map((form) => form.title)

  const introOffer = sitesInIntroOffer?.find((s) => s === siteId)
  const isSuccess = status?.toUpperCase() === SEVERITY.SUCCESS.toUpperCase()

  const isFirstSessionInLocation = isFirstSession({
    siteId,
    checkInDate: checkInTime,
    siteInformation,
    isSuccess,
  })
  const getAlerts = () => {
    const alerts = []

    if (payments?.failed?.length > 0 /* || payments?.outstanding?.length > 0 */) {
      alerts.push(ALERT_TYPES.PAYMENT)
    }
    filteredFormsPending?.length > 0 && alerts.push(ALERT_TYPES.FORM)
    if (hasNotesAndProperties) {
      alerts.push(ALERT_TYPES.NOTE)
    }
    !isHomeLocation && alerts.push(ALERT_TYPES.HOME)
    birthday && alerts.push(ALERT_TYPES.BIRTHDAY)
    packagesData?.clientSiteActivePackages?.length > 0 && alerts.push(ALERT_TYPES.PACKAGE)
    isFirstSessionInLocation && alerts.push(ALERT_TYPES.FIRST_SESSION)
    introOffer && alerts.push(ALERT_TYPES.INTRO_OFFER)

    if (alerts?.length > 0) {
      return alerts
    } else {
      return false
    }
  }
  return (
    <Box sx={[styles.itemWrapper, isOpen && styles.itemWrapperOpened]}>
      <AnimatedBox style={fadeAnimationStyles}>
        <BannerAlert
          message={message}
          description={description}
          extraInfo={date}
          failReason={failReason}
          severity={severity}
          size={SIZE.SMALL}
          themedMessage
          hasExtraDivider
          hasBackground
          isLoading={isStatusUnknownAndOpen}
        />
      </AnimatedBox>
      <Box
        sx={[
          styles.contentContainer,
          styles.contentContainerAnimated,
          styles.columnGap,
          isOpen && styles.contentContainerAnimatedSpacing,
          isOpen && styles.gridTemplate,
          isOpen && styles.openedSpacing,
          isOpen && styles.openedColumnGap,
          index === 0 && !isOpen && styles.firstItemClosed,
        ]}
      >
        <Box sx={styles.avatarHolderAnimated}>
          <Tooltip
            disableFocusListener={!isOpen || hasNoClientRecord}
            disableHoverListener={!isOpen || hasNoClientRecord}
            disableTouchListener={!isOpen || hasNoClientRecord}
            title={!hasNoClientRecord && t('checkIn:changeImage')}
            placement={TOOLTIP_POSITION.bottom}
          >
            <Box {...(isOpen && { sx: styles.avatarOverlay })}>
              <SvgIcon sx={styles.avatarIcon} component={cameraIcon} viewBox="0 0 16 17" />
              <Avatar
                variant={AVATAR_VARIANT.CIRCULAR}
                item={{
                  alt: `${name}'s avatar`,
                }}
                src={profileImg?.url}
                onClick={() =>
                  isOpen &&
                  !hasNoClientRecord &&
                  pushDialog(
                    ModalUploadPhoto,
                    { mutation: clientImageMutation },
                    {
                      dialogFilterParams: {
                        key: 'title',
                        value: CHECK_IN_TITLE,
                      },
                    }
                  )
                }
                avatarType={AVATAR_TYPES.SINGLE_AVATAR}
                size={AVATAR_SIZE.LARGE}
                animationStyles={avatarAnimationStyles}
              />
            </Box>
          </Tooltip>
        </Box>
        <Box sx={styles.nameAndViewProfileContainer}>
          <Box onClick={() => setIsOpen(!isOpen)} sx={[styles.flexSpaceBetween, styles.pointer]}>
            <Box sx={styles.bannerAndNameContainer}>
              <AnimatedBox style={revertedFadeAnimationStyles}>
                <BannerAlert
                  message={message}
                  description={description}
                  extraInfo={date}
                  failReason={failReason}
                  severity={severity}
                  size={SIZE.SMALL}
                  themedMessage
                  hasExtraDivider
                  hasFailReasonDivider
                  isLoading={isStatusUnknownAndClosed}
                />
              </AnimatedBox>
              <Typography typography="header3" color="gray.100">
                {name}
              </Typography>
            </Box>
            <AnimatedBox
              style={rotateAnimationStyles}
              display="flex"
              sx={styles.arrowIconRightSpace}
            >
              <SvgIcon component={AngleDownIcon} sx={styles.arrowIcon} />
            </AnimatedBox>
          </Box>
          <AnimatedBox style={fadeAnimationStyles}>
            <Box>
              <Button
                variant="text"
                textPadding={false}
                href={profileUrl}
                disabled={hasNoClientRecord}
              >
                {t('checkIn:viewProfile')}
              </Button>
            </Box>
          </AnimatedBox>
        </Box>

        <Box sx={[styles.infoContainer]}>
          <AnimatedBox style={fadeAnimationStyles}>
            <Stack spacing={{ xs: 2, md: 3 }}>
              <Grid container rowSpacing={{ xs: 1, sm: 0 }} columnSpacing={3}>
                <Grid item xs={12} sx={styles.locationSession}>
                  {isStatusUnknownAndOpen ? (
                    <>
                      <Skeleton
                        variant={SKELETON_VARIANTS.circular}
                        sx={styles.smallCircularSkeleton}
                      />
                      <Skeleton
                        isRounded
                        variant={SKELETON_VARIANTS.rectangular}
                        sx={styles.rectangularSkeleton}
                      />
                    </>
                  ) : (
                    <>
                      <SvgIcon
                        component={packages}
                        sx={membership ? styles.packagesIcon : styles.noPackagesIcon}
                      />
                      <Typography typography="medium" color={membership ? 'gray.64' : 'gray.40'}>
                        {membership ? membership.title : t('checkIn:noMembership')}
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid
                  container
                  item
                  xs={12}
                  sm={locationWidth}
                  wrap="nowrap"
                  sx={styles.locationSession}
                >
                  {isStatusUnknownAndOpen ? (
                    <>
                      <Skeleton
                        variant={SKELETON_VARIANTS.circular}
                        sx={styles.smallCircularSkeleton}
                      />
                      <Skeleton
                        isRounded
                        variant={SKELETON_VARIANTS.rectangular}
                        sx={styles.rectangularSkeleton}
                      />
                    </>
                  ) : (
                    <>
                      <SvgIcon
                        component={pinLocation}
                        sx={location ? styles.packagesIcon : styles.noPackagesIcon}
                      />
                      <Typography typography="medium" color={location ? 'gray.64' : 'gray.40'}>
                        {location ? location : t('checkIn:noLocation')}
                      </Typography>
                    </>
                  )}
                </Grid>

                <Grid
                  container
                  item
                  xs={12}
                  sm={sessionWidth}
                  wrap="nowrap"
                  sx={styles.locationSession}
                >
                  {isStatusUnknownAndOpen ? (
                    <>
                      <Skeleton
                        variant={SKELETON_VARIANTS.circular}
                        sx={styles.smallCircularSkeleton}
                      />
                      <Skeleton
                        isRounded
                        variant={SKELETON_VARIANTS.rectangular}
                        sx={styles.rectangularSkeleton}
                      />
                    </>
                  ) : (
                    <>
                      {nextSession ? (
                        <>
                          <SvgIcon component={schedule} sx={styles.packagesIcon} />
                          <Box>
                            <Typography component="span" sx={styles.membershipField}>
                              {nextSession.title}
                            </Typography>
                            <Typography component="span" sx={styles.circle}>
                              {' • '}
                            </Typography>
                            <Typography component="span" sx={styles.membershipField}>
                              {format(
                                utcToZonedTime(new Date(nextSession.datetime), currentTimezone),
                                `${
                                  timeFormatData?.timeFormat === TIME_FORMATS.T12.value
                                    ? TIME_FORMATS.T12.pattern
                                    : TIME_FORMATS.T24.pattern
                                }:mm aaa`
                              )}
                            </Typography>
                          </Box>
                        </>
                      ) : (
                        <>
                          <SvgIcon
                            color="gray.40"
                            component={schedule}
                            sx={styles.noPackagesIcon}
                          />
                          <Box>
                            <Typography component="span" typography="medium" color="gray.40">
                              {t('checkIn:noSessions')}
                            </Typography>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1.3} sx={styles.profileOptions}>
                <Button
                  variant={BUTTON_VARIANTS.OUTLINED}
                  size={BUTTON_SIZE.SMALL}
                  theme={BUTTON_THEME.GRAY}
                  startIcon={note}
                  disabled={hasNoClientRecord}
                  onClick={() =>
                    pushDialog(AddNoteDialog, {
                      client: { id: clientId, name, firstName },
                    })
                  }
                >
                  {t('checkIn:note.addNote')}
                </Button>
                <Button
                  variant={BUTTON_VARIANTS.ICON}
                  size={BUTTON_SIZE.SMALL}
                  icon={shoppingCart}
                  stroke
                  href={saleUrl}
                  disabled={hasNoClientRecord}
                  sx={[styles.cartAndAddNoteButtons, styles.cartButton]}
                />
                <Box sx={styles.badgesWrapper}>
                  {isStatusUnknownAndOpen ? (
                    <Box sx={styles.flexRowCentered}>
                      <Skeleton
                        variant={SKELETON_VARIANTS.circular}
                        sx={styles.mediumCircularSkeleton}
                      />
                      <Skeleton
                        variant={SKELETON_VARIANTS.circular}
                        sx={{
                          ...styles.mediumCircularSkeleton,
                          ...styles.badgeSkeletonSpacing,
                        }}
                      />
                    </Box>
                  ) : (
                    <Badges
                      checkInDate={checkInTime}
                      totalCheckInCount={totalCheckInCount}
                      siteInformation={siteInformation}
                      isSuccess={isSuccess}
                    />
                  )}
                </Box>
              </Stack>
            </Stack>
          </AnimatedBox>
        </Box>
      </Box>

      {isStatusUnknownAndOpen ? (
        <>
          <Box sx={styles.alertsSkeletonContainer}>
            <Skeleton variant={SKELETON_VARIANTS.circular} sx={{ height: 20, width: 20 }} />
            <Skeleton isRounded sx={{ marginLeft: 1 }} variant={SKELETON_VARIANTS.rectangular} />
          </Box>
          <Box sx={styles.alertsSkeletonContainer}>
            <Box sx={styles.skeletonGroup}>
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.mediumCircularSkeleton} />
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.skeletonGroupItem} />
              <Skeleton variant={SKELETON_VARIANTS.circular} sx={styles.skeletonSecondGroupItem} />
            </Box>

            <Skeleton isRounded sx={{ marginLeft: 3.5 }} variant={SKELETON_VARIANTS.rectangular} />
          </Box>
        </>
      ) : (
        <>
          {isOpen && !hasNoClientRecord && (
            <AnimatedBox style={fadeAnimationStyles}>
              <Stack divider={<Divider flexItem />} sx={styles.alertsWrapper}>
                {payments?.failed?.length > 0 && (
                  <PaymentsList type={PAYMENT_TYPE.FAILED} payments={payments?.failed} />
                )}
                {/* {payments?.outstanding?.length > 0 && (
                  <PaymentsList type={PAYMENT_TYPE.OUTSTANDING} payments={payments?.outstanding} />
                )} */}

                {getAlerts() && (
                  <Grid item>
                    <ViewAllAlerts
                      hasTextLink
                      alerts={getAlerts()}
                      onClick={() =>
                        pushDialog(ModalAlerts, {
                          siteId: resolvedSiteId,
                          profileImg,
                          name,
                          firstName,
                          failedPayments: payments?.failed,
                          // outstandingPayments: payments?.outstanding,
                          clientId,
                          hasNotesAndProperties,
                          birthday,
                          formsPending: filteredFormsPending,
                          isFirstSession: isFirstSessionInLocation,
                          introOffer,
                          location,
                          ...(!isHomeLocation && {
                            homeLocations: homeLocationNames,
                          }),
                        })
                      }
                    />
                  </Grid>
                )}
              </Stack>
            </AnimatedBox>
          )}
        </>
      )}
      {isOpen && hasTanningFeatures && (
        <EquipmentsAvailable clientId={clientId} clientName={name} />
      )}
    </Box>
  )
}

MostRecentCardAnimated.propTypes = {
  clientId: PropTypes.string.isRequired,
  profileImg: PropTypes.shape({
    url: PropTypes.string,
  }).isRequired,
  name: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  membership: PropTypes.shape({
    title: PropTypes.string,
  }),
  location: PropTypes.string,
  nextSession: PropTypes.shape({
    title: PropTypes.string,
    datetime: PropTypes.string,
  }),
  checkInTime: PropTypes.string,
  payments: PropTypes.shape({
    outstanding: PropTypes.array,
    failed: PropTypes.array,
  }),
  isInitiallyOpen: PropTypes.bool,
  failReason: PropTypes.string,
  index: PropTypes.number,
  birthday: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  homeSites: PropTypes.arrayOf(PropTypes.string),
  totalCheckInCount: PropTypes.number,
  siteInformation: PropTypes.arrayOf(
    PropTypes.shape({
      siteId: PropTypes.string,
      lastCheckInOn: PropTypes.string,
      checkInCount: PropTypes.number,
    })
  ),
  siteNotes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),
  siteAlertedProperties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),
  sitesInIntroOffer: PropTypes.array,
  formsPending: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      siteId: PropTypes.string,
    })
  ),
  checkInId: PropTypes.string,
}

MostRecentCardAnimated.defaultProps = {
  siteNotes: [],
  siteAlertedProperties: [],
}

export default MostRecentCardAnimated
